// src/pages/IntakeHub/NewChildSheet.js
import React, { useState } from 'react';
import DisabilityPicker from './DisabilityPicker';
import { createChildWithCase, LANGUAGES, ageLabel } from './caseHistoryApi';

// Five required fields open an episode. Everything else the Case History asks later.
const EMPTY = {
    name: '', date_of_birth: '', gender: '', primary_language: 'Hindi', supervising_therapist_id: '',
    disabilities: [], disability_notes: '', guardian_name: '', guardian_phone: '', guardian_email: '',
};

function monthsSince(dob) {
    if (!dob) return null;
    const birth = new Date(dob);
    if (Number.isNaN(birth.getTime())) return null;
    const now = new Date();
    let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
    if (now.getDate() < birth.getDate()) months -= 1;
    return Math.max(months, 0);
}

export default function NewChildSheet({ therapists, onClose, onCreated }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [duplicate, setDuplicate] = useState(null);

    const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
    const months = monthsSince(form.date_of_birth);
    const ready = form.name.trim() && form.date_of_birth && form.gender
        && form.primary_language && form.supervising_therapist_id;

    const submit = async (confirmDuplicate) => {
        setSaving(true);
        setError('');
        try {
            const { data } = await createChildWithCase({
                ...form,
                name: form.name.trim(),
                supervising_therapist_id: Number(form.supervising_therapist_id),
                disabilities: form.disabilities.length ? form.disabilities : null,
                confirm_duplicate: !!confirmDuplicate,
            });
            onCreated(data);
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            if (status === 409 && detail?.existing_child) {
                setDuplicate(detail.existing_child);
            } else {
                setError(typeof detail === 'string' ? detail : 'Could not create the child. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content ih-sheet" onClick={(e) => e.stopPropagation()}>
                <div className="ih-row" style={{ marginBottom: 14 }}>
                    <h2 style={{ margin: 0 }}>New Child</h2>
                    <span className="ih-sub">Opens Episode 1 as a draft</span>
                </div>

                {error && <div className="ih-note red" style={{ marginBottom: 12 }}>{error}</div>}

                {duplicate && (
                    <div className="ih-note amber" style={{ marginBottom: 12 }}>
                        <strong>A child named &ldquo;{duplicate.name}&rdquo; already exists</strong> in this organisation
                        {duplicate.date_of_birth ? ` — born ${duplicate.date_of_birth}` : ''}
                        {duplicate.guardian_phone ? `, guardian ${duplicate.guardian_phone}` : ''}. Is this the same child?
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button className="btn btn-secondary" onClick={onClose}>Yes — same child, cancel</button>
                            <button className="btn btn-primary" disabled={saving} onClick={() => submit(true)}>
                                No — a different child
                            </button>
                        </div>
                    </div>
                )}

                <div className="ih-grid2">
                    <div className="form-group">
                        <label>Child name <span className="ih-req">*</span></label>
                        <input value={form.name} onChange={set('name')} autoFocus />
                    </div>
                    <div className="form-group">
                        <label>Date of birth <span className="ih-req">*</span></label>
                        <input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
                        {months !== null && <div className="ih-hint">Age {ageLabel(months)}</div>}
                    </div>
                </div>

                <div className="ih-grid2">
                    <div className="form-group">
                        <label>Gender <span className="ih-req">*</span></label>
                        <select value={form.gender} onChange={set('gender')}>
                            <option value="">Select</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Primary language <span className="ih-req">*</span></label>
                        <select value={form.primary_language} onChange={set('primary_language')}>
                            {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label>Supervising therapist <span className="ih-req">*</span></label>
                    <select value={form.supervising_therapist_id} onChange={set('supervising_therapist_id')}>
                        <option value="">Select</option>
                        {therapists.map((t) => <option key={t.id} value={t.id}>{t.username}</option>)}
                    </select>
                    <div className="ih-hint">Nothing in the Case History can be saved until this is set.</div>
                </div>

                <div className="form-group">
                    <label>Disability (if any)</label>
                    <DisabilityPicker
                        value={form.disabilities}
                        onChange={(disabilities) => setForm({ ...form, disabilities })}
                    />
                    <div className="ih-hint">Optional. A child may have more than one.</div>
                </div>

                {form.disabilities.length > 0 && (
                    <div className="form-group">
                        <label>Disability notes</label>
                        <input value={form.disability_notes} onChange={set('disability_notes')} />
                    </div>
                )}

                <div className="ih-grid2">
                    <div className="form-group">
                        <label>Guardian name</label>
                        <input value={form.guardian_name} onChange={set('guardian_name')} />
                    </div>
                    <div className="form-group">
                        <label>Guardian phone</label>
                        <input value={form.guardian_phone} onChange={set('guardian_phone')} />
                    </div>
                </div>

                <div className="form-group">
                    <label>Guardian email</label>
                    <input type="email" value={form.guardian_email} onChange={set('guardian_email')} />
                    <div className="ih-hint">Used later to send the consent copy and the summary.</div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" disabled={!ready || saving} onClick={() => submit(false)}>
                        {saving ? 'Creating…' : 'Create draft'}
                    </button>
                </div>
            </div>
        </div>
    );
}
